const csrfStorageKey = 'details-ai-csrf';
let refreshing: Promise<boolean> | null = null;

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export function setCsrfToken(token: string | null) {
  if (token) sessionStorage.setItem(csrfStorageKey, token);
  else sessionStorage.removeItem(csrfStorageKey);
}

function csrfToken() {
  return sessionStorage.getItem(csrfStorageKey);
}

async function parseError(response: Response) {
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
  };
  return new ApiClientError(
    data.message || 'تعذر إكمال الطلب',
    data.error || 'REQUEST_FAILED',
    response.status,
  );
}

async function refreshAccessToken() {
  if (!refreshing) {
    refreshing = (async () => {
      const token = csrfToken();
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'X-CSRF-Token': token } : {},
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { csrfToken: string };
      setCsrfToken(data.csrfToken);
      return true;
    })().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

export async function api<T>(
  path: string,
  options: RequestInit & { skipRefresh?: boolean } = {},
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) headers.set('X-CSRF-Token', token);
  }
  const { skipRefresh, ...requestOptions } = options;
  let response = await fetch(path, {
    ...requestOptions,
    method,
    headers,
    credentials: 'include',
  });
  if (
    response.status === 401 &&
    !skipRefresh &&
    !path.includes('/auth/refresh') &&
    !path.includes('/auth/login') &&
    !path.includes('/otp/verify')
  ) {
    if (await refreshAccessToken()) {
      response = await fetch(path, {
        ...requestOptions,
        method,
        headers,
        credentials: 'include',
      });
    }
  }
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function streamApi(
  path: string,
  body: unknown,
  handlers: {
    onToken: (token: string) => void;
    onDone: (data: { messageId: string; provider: string; modelId: string }) => void;
    onError: (message: string) => void;
  },
) {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken() ? { 'X-CSRF-Token': csrfToken()! } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  if (!response.body) throw new Error('STREAM_UNAVAILABLE');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';
    for (const event of events) {
      const type = event.match(/^event:\s*(.+)$/m)?.[1]?.trim();
      const dataLine = event.match(/^data:\s*(.+)$/m)?.[1]?.trim();
      if (!type || !dataLine) continue;
      const data = JSON.parse(dataLine) as Record<string, string>;
      if (type === 'token') handlers.onToken(data.token ?? '');
      if (type === 'done') {
        handlers.onDone({
          messageId: data.messageId ?? '',
          provider: data.provider ?? '',
          modelId: data.modelId ?? '',
        });
      }
      if (type === 'error') handlers.onError(data.message || 'تعذر إكمال الطلب');
    }
  }
}
